/**
 * @module CustomerPromotionService
 * @remarks RTK Query service for customer promotion data
 */

import { CustomerPromotions } from "@models/customer-promotions.model";
import { createApi, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { RootState } from "@store/store";

// Define a simple interface for the profile data structure
interface CustomerProfile {
  profile?: {
    promotions?: Array<{
      id: string;
      promotion_id?: number;
      kunde?: { id: string };
      redeemed: boolean | null;
      kundenbetreuung: null;
      ja_nein: null;
      text: string;
      valid: string | boolean;
      active?: boolean | null;
      required_points?: string;
      [key: string]: unknown;
    }>;
  };
}

/**
 * API service for managing customer promotion data
 */
export const CustomerPromotionApi = createApi({
  reducerPath: "customerPromotionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["CustomerPromotion"],
  endpoints: (builder) => ({
    getCustomerPromotions: builder.query<CustomerPromotions[], string>({
      async queryFn(customer_number: string, { getState }: { getState: () => unknown }): Promise<{ data: CustomerPromotions[] } | { error: FetchBaseQueryError }> {
        // Only use cached data from userApi - no more direct API calls
        const state = getState() as RootState;
        
        // Find all getCustomerData queries in the cache
        const queries = state.userApi?.queries || {};
        let promotionsFromCache = null;
        
        // Look for customer data that might contain promotions
        for (const queryKey in queries) {
          if (queryKey.startsWith('getCustomerData') && queries[queryKey]?.data) {
            const data = queries[queryKey].data as CustomerProfile;
            if (data?.profile?.promotions) {
              promotionsFromCache = data.profile.promotions;
              break;
            }
          }
        }
        
        // If we found promotions in the cache, return them
        if (promotionsFromCache) {
          // Transform the promotions data to match the CustomerPromotions structure
          const formattedPromotions: CustomerPromotions[] = [{
            id: customer_number,
            md: 0,
            kunden: [{
              id: customer_number,
              kundenpromotions: promotionsFromCache
            }]
          }];
          return { data: formattedPromotions };
        }
        
        // No cache data available - return empty array (userApi should be called first)
        return { data: [] };
      },
      providesTags: ["CustomerPromotion"],
      // Zwischenspeichern für einen Tag (86.400 Sekunden)
      keepUnusedDataFor: 86400,
    }),
    invalidateCustomerPromotions: builder.mutation<void, void>({
      queryFn: () => ({ data: undefined }),
      invalidatesTags: ["CustomerPromotion"],
    }),
  }),
});

// Export hooks für Verwendung in Komponenten
export const {
  useGetCustomerPromotionsQuery,
  useInvalidateCustomerPromotionsMutation,
} = CustomerPromotionApi;
